/**
 * Since conversations layer, this composable is only used for email for the time being.
 */

import { StatusFilterInterface } from "@/composables/v2.1/useEmailStatusFilters";
import { useInboxModuleType } from "@/composables/v2.1/useInboxModuleType";
import ChatLastEntryInterface from "@/interfaces/inbox/chatLastEntry.interface";
import ChatsListChatInterface from "@/interfaces/inbox/chatsListChat.interface";
import axios from "@/libs/axios";
import { resolveChatStatusFilter } from "@/utils/chats";
import { computed, reactive, Ref, UnwrapRef } from "@vue/composition-api";
import { isEqual } from "lodash";

const state: UnwrapRef<{ chats: ChatsListChatInterface[] | undefined; loading: boolean }> = reactive({
    chats: [],
    loading: false,
    error: null,
});

export default function useChats() {
    const { publicApiEndpointPath } = useInboxModuleType();

    const clearChats = () => {
        state.chats = [];
    };

    // @ts-ignore
    const fetchChats = (queryParams): Promise<ChatsListChatInterface[] | undefined> => {
        return axios
            .get(publicApiEndpointPath.value, {
                params: {
                    fields: "contactId,channelId,platform,identity",
                    sort: "createdAt,DESC",
                    s: JSON.stringify(queryParams.s),
                    //page: queryParams.page,
                    page: 1,
                    limit: 200,
                },
            })
            .then((response) => {
                return (
                    response.data
                        //.filter((chat: chatsListChatInterface) => chat)
                        .map((chat: ChatsListChatInterface) => {
                            //chat.statusFilter = resolveChatStatusFilter(chat, teamsOfUser.value);
                            chat.statusFilter = resolveChatStatusFilter(chat); // @DENNIS 2023-03 This param was unused in the function itself, so commented.
                            return chat;
                        })
                );
            });
    };

    const fetchOneChat = (chatId: number): Promise<ChatsListChatInterface | undefined> => {
        return axios
            .get(publicApiEndpointPath.value, {
                params: {
                    s: {
                        id: {
                            $eq: chatId,
                        },
                    },
                },
            })
            .then((response) => {
                const chat = response.data[0];

                if (!chat) {
                    return;
                }

                //chat.statusFilter = resolveChatStatusFilter(chat, teamsOfUser.value);
                chat.statusFilter = resolveChatStatusFilter(chat); // @DENNIS 2023-03 This param was unused in the function itself, so commented.

                return chat;
            });
    };

    // @ts-ignore
    const loadChats = async (
        filter: Ref<{ status: StatusFilterInterface; keyword: string; page: number }>,
        options: { isInitialLoading: boolean } = { isInitialLoading: true }
    ) => {
        let searchQueryString = {};

        if (filter.value.status?.query) {
            searchQueryString = { ...filter.value.status?.query };
        }

        if (filter.value.keyword?.length > 0) {
            // @ts-ignore
            searchQueryString["keyword"] = filter.value.keyword;
        }

        state.loading = options.isInitialLoading;
        state.chats =
            (await fetchChats({ s: searchQueryString, page: filter.value.page }).finally(
                () => (state.loading = false)
            )) || [];

        // We need to return, so the promise will be resolved in order for vue-query to work.
        return state.chats;
    };

    const updateChatInChatsList = async (
        chatId: number,
        chatData: ChatsListChatInterface,
        index?: number
    ): Promise<
        | {
              originalData: ChatsListChatInterface | undefined;
              updatedData: ChatsListChatInterface | undefined;
          }
        | undefined
    > => {
        if (!chatId || !state.chats) {
            return;
        }

        const chatsListIndex = index ?? state.chats.findIndex((chat: ChatsListChatInterface) => chat.id === chatId);

        if (chatsListIndex !== -1) {
            const updatedData = { ...state.chats[chatsListIndex], ...chatData };

            if (isEqual(updatedData, state.chats[chatsListIndex])) {
                return {
                    originalData: state.chats[chatsListIndex],
                    updatedData: undefined,
                };
            }

            const originalData = state.chats.splice(chatsListIndex, 1, updatedData)[0];

            return {
                originalData,
                updatedData,
            };
        }

        // Fetch the conversation if it is not found
        const chat = await fetchOneChat(chatId);

        if (!chat) {
            return;
        }

        addChatToChatsList(chat);

        return {
            originalData: undefined,
            updatedData: chat,
        };
    };

    const addChatToChatsList = (chat: ChatsListChatInterface): void => {
        state.chats?.push(chat);
    };

    /*
    Sets the last entry message of the corresponding chat in the chats list
     */
    const setChatsListLastEntry = (chatId: number, chatEntry: ChatLastEntryInterface, isActiveChat?: boolean): void => {
        if (!state.chats) {
            return;
        }

        const chat: ChatsListChatInterface | undefined = state.chats.find(
            (chat: ChatsListChatInterface) => chat.id === chatId
        );

        if (chat && chat.lastConversation) {
            chat.lastConversation.lastEntry = chatEntry;

            if (!isActiveChat && typeof chat.unreadMessages !== "undefined") {
                chat.unreadMessages += 1;
            }
        }
    };

    const deleteChatFromChatsList = (chatId: number) => {
        if (!state.chats) {
            return;
        }

        state.chats = state.chats.filter((chat) => chat.id !== chatId);
    };

    return {
        getChats: computed(() => state.chats),
        isLoading: computed(() => state.loading),
        loadChats,
        updateChatInChatsList,
        setChatsListLastEntry,
        addChatToChatsList,
        deleteChatFromChatsList,
        clearChats,
    };
}
